import {
  BOOKING_FILTERS,
  PASSENGER_FILTERS,
  type BookingEntity,
  type ITypeormWhereCriteria,
  type ObjectLiteral,
  type OperatorDescriptor,
  type PassengerEntity,
} from "@ryde/shared";

const asOperatorWhere = (where: object): ObjectLiteral<OperatorDescriptor> =>
  where as ObjectLiteral<OperatorDescriptor>;

const readInFilter = ({
  filters,
  key,
}: {
  filters: object | undefined;
  key: string;
}): OperatorDescriptor | undefined => {
  const filter = asOperatorWhere(filters ?? {})[key];
  return filter?.$in ? filter : undefined;
};

const assignPassengerInFilter = ({
  passengersWhere,
  key,
  filter,
}: {
  passengersWhere: ITypeormWhereCriteria<PassengerEntity>;
  key: string;
  filter?: OperatorDescriptor;
}): void => {
  if (!filter?.$in) {
    return;
  }

  asOperatorWhere(passengersWhere)[key] = filter;
};

/** Passenger `where` for the booking calendar rows, derived from the booking grid filters. */
export const buildBookingPassengersWhere = (
  bookingFilters: ITypeormWhereCriteria<BookingEntity> | undefined,
): ITypeormWhereCriteria<PassengerEntity> => {
  const passengersWhere: ITypeormWhereCriteria<PassengerEntity> = {};

  if (bookingFilters?.passengerId) {
    passengersWhere.id = bookingFilters.passengerId;
  }

  const passengerFilterSubSchema = bookingFilters?.passenger as
    ITypeormWhereCriteria<PassengerEntity> | undefined;

  if (passengerFilterSubSchema?.siteId) {
    passengersWhere.siteId = passengerFilterSubSchema.siteId;
  }

  if (passengerFilterSubSchema?.unitId) {
    passengersWhere.unitId = passengerFilterSubSchema.unitId;
  }

  if (passengerFilterSubSchema?.passengerTypeId) {
    passengersWhere.passengerTypeId = passengerFilterSubSchema.passengerTypeId;
  }

  if (passengerFilterSubSchema?.isEligible) {
    passengersWhere.isEligible = passengerFilterSubSchema.isEligible;
  }

  if (passengerFilterSubSchema?.isActive) {
    passengersWhere.isActive = passengerFilterSubSchema.isActive;
  }

  assignPassengerInFilter({
    passengersWhere,
    key: PASSENGER_FILTERS.disableHoursIds,
    filter: readInFilter({
      filters: passengerFilterSubSchema,
      key: PASSENGER_FILTERS.disableHoursIds,
    }),
  });

  assignPassengerInFilter({
    passengersWhere,
    key: PASSENGER_FILTERS.referentId,
    filter: readInFilter({
      filters: passengerFilterSubSchema,
      key: PASSENGER_FILTERS.referentId,
    }),
  });

  // Booking Origin/Destination area stay on the bookings grid. Passenger Home / Destination
  // area are separate filters and are forwarded as reserved passenger keys.
  assignPassengerInFilter({
    passengersWhere,
    key: PASSENGER_FILTERS.sourceAreaId,
    filter: readInFilter({
      filters: bookingFilters,
      key: BOOKING_FILTERS.passengersHomeAreaId,
    }),
  });

  assignPassengerInFilter({
    passengersWhere,
    key: PASSENGER_FILTERS.destinationAreaId,
    filter: readInFilter({
      filters: bookingFilters,
      key: BOOKING_FILTERS.passengersDestinationAreaId,
    }),
  });

  return passengersWhere;
};
